import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { NotificationsContext } from "@ui-components/Notifications";
import { useIntl, FormattedMessage } from "react-intl";
import apiRequest from "@services/apiRequest";
import AuthenticationService from "../../../services/AuthenticationService";

import Input from "@ui-components/Input";
import Select from "@ui-components/Select";
import Button from "@ui-components/Button";

import UserTypeSelect from "@components/UserTypeSelect";

export default function EditRoleComponent({ userData, onSubmit, externalRoles }) {
  const intl = useIntl();
  const { push } = useContext(NotificationsContext);

  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState(userData.firstname || "");
  const [lastName, setLastName] = useState(userData.lastname || "");
  const [company, setCompany] = useState(userData.company || "");
  const [vat, setVat] = useState(userData.vat || "");
  const [role, setRole] = useState(`user_type_${userData.role_type}` || "");
  const [sector, setSector] = useState(
    userData.sector || userData.stakeholder || " "
  );
  const [qualification, setQualification] = useState(
    userData.qualification || " "
  );
  const [editableRoles, setEditableRoles] = useState([]);

  const formRef = useRef(null);

  const getRoleType = () => {
    return role.split("user_type_")[1];
  };

  const userKeys = [
    {
      visible: true,
      key: "role_type",
    },
    {
      visible: true,
      key: "firstname",
    },
    {
      visible: true,
      key: "lastname",
    },
    {
      visible: true,
      key: "email",
    },
    {
      visible: !["resale", "resale_tintometer"].includes(getRoleType()),
      key: "city",
    },
    {
      visible: !["resale", "resale_tintometer"].includes(getRoleType()),
      key: "zip_code",
    },
    {
      visible: !["freelance"].includes(getRoleType()),
      key: "company",
    },
    {
      visible: !["freelance"].includes(getRoleType()),
      key: "vat",
    },
    {
      visible: !["freelance"].includes(getRoleType()),
      key: "qualification",
    },
    {
      visible: true,
      key: "stakeholder",
    },
  ];

  const sectorOptions = {
    user_type_design_studio: [
      " ",
      "architecture",
      "engineering",
      "interior_design",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_enterprise: [
      " ",
      "structural_reinforcement",
      "waterproofing",
      "vertical_works",
      "construction",
      "civil_works_specialist",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
  };

  const qualificationOptions = {
    user_type_design_studio: [
      " ",
      "engineer",
      "architect",
      "owner",
      "surveyor",
      "decoration/interior_design",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_enterprise: [
      " ",
      "engineer",
      "architect",
      "owner",
      "surveyor",
      "applicator",
      "other",
    ].map((i) => ({ value: i, name: intl.formatMessage({ id: i }) })),
    user_type_resale_tintometer: [" ", "owner", "other"].map((i) => ({
      value: i,
      name: intl.formatMessage({ id: i }),
    })),
    user_type_resale: [" ", "owner", "other"].map((i) => ({
      value: i,
      name: intl.formatMessage({ id: i }),
    })),
  };

  function generatePostData() {
    // Per semplificare l'accesso ai dati correnti
    const isVisible = (key) => {
      const field = userKeys.find((k) => k.key === key);
      return field ? !!field.visible : false;
    };
    // Oggetto `postData` generato in base alla visibilità dei campi

    const role_id = editableRoles.find(
      (r) => r.name === role.split("user_type_")[1]
    )?.id;

    return {
      countries: userData.countries,
      role_id: role_id,
      show_fix_role_warning: null,
      firstname: isVisible("firstname") ? firstName : null,
      lastname: isVisible("lastname") ? lastName : null,
      company: isVisible("company") ? company : null,
      vat: isVisible("vat") ? vat : null,
      stakeholder:
        isVisible("stakeholder") &&
        [
          "user_type_design_studio",
          "user_type_enterprise",
          "user_type_freelance",
        ].includes(role)
          ? sector
          : null,
      qualification:
        isVisible("qualification") &&
        ["user_type_design_studio", "user_type_enterprise"].includes(role)
          ? qualification
          : null,
    };
  }

  const _onSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (!role) {
        push({
          title: intl.formatMessage({ id: "need_to_select_a_role" }),
          type: "error",
        });
        return;
      }
      if (
        ["user_type_design_studio", "user_type_enterprise"].includes(role) &&
        (!company || sector === " " || qualification === " ")
      ) {
        push({
          title: intl.formatMessage({ id: "enter_company_data" }),
          type: "error",
        });
        return;
      }
      if (["user_type_freelance"].includes(role) && sector === " ") {
        push({
          title: intl.formatMessage({ id: "enter_specialization" }),
          type: "error",
        });
        return;
      }
      if (!firstName || !lastName) {
        push({
          title: intl.formatMessage({ id: "enter_name" }),
          type: "error",
        });
        return;
      }

      setLoading(true);
      generatePostData();
      const postData = generatePostData();
      apiRequest
        .get(`/users/${userData.id}`)
        .then((res) => {
          const data = { ...res, ...postData };
          apiRequest.post(`/users/${userData.id}`, data).then(() => {
            apiRequest.get("/users/refresh_token").then((res) => {
              AuthenticationService.setAuthToken(
                res.access_token,
                res.user_data
              );
              setLoading(false);
              onSubmit();

              push({
                title: intl.formatMessage({
                  id: "user_updated_successfully",
                }),
                type: "success",
              });
            });
          });
        })
        .catch((error) => {
          console.error(error);
          push({
            title: intl.formatMessage({ id: "server_error" }),
            type: "error",
          });
        });
    },
    // eslint-disable-next-line
    [firstName, lastName, role, sector, qualification, editableRoles]
  );

  useEffect(async () => {
    const fetchUserTypes = async () => {
      setLoading(true);
      try {
        const [resEditableRoles] = await Promise.all([
          apiRequest.get(`/users/roles`),
        ]);
        setEditableRoles(resEditableRoles);
      } catch (error) {
        console.log("errore", error);
      } finally {
        setLoading(false);
      }
    };
    await fetchUserTypes();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className="px-4 sm:px-8 py-10">
        <form
          action="#"
          method="POST"
          onSubmit={_onSubmit}
          ref={formRef}
          className="w-full"
        >
          {/* ----- User type selection -----*/}
          <div className="my-7">
            <p>
              <FormattedMessage id="select_user_type" />
            </p>

            {externalRoles.length > 0 && (
              <div className="my-4">
                <UserTypeSelect
                  value={role}
                  onClick={setRole}
                  userTypes={externalRoles
                    .filter(
                      (x) =>
                        x.active ||
                        !!x.associated_roles_ids?.includes(userData.role_id)
                    )
                    .sort((a, b) => a.order - b.order)}
                />
              </div>
            )}
          </div>
          {!!role && (
            <>
              <div className="grid grid-cols-2 gap-3 w-full">
                {[
                  "user_type_design_studio",
                  "user_type_enterprise",
                  "user_type_resale_tintometer",
                  "user_type_resale",
                ].includes(role) && (
                  <>
                    <div className={"md:col-span-1"}>
                      <Input
                        value={company}
                        onChange={(e) => {
                          setCompany(e.target.value);
                        }}
                        label={intl.formatMessage({ id: "company_name" })}
                        type="text"
                        errorMessage={intl.formatMessage({
                          id: "enter_company_name",
                        })}
                        required
                      />
                    </div>
                    <div className={"md:col-span-1"}>
                      <Input
                        value={vat}
                        onChange={(e) => {
                          setVat(e.target.value);
                        }}
                        label={intl.formatMessage({ id: "vat" })}
                        type="text"
                        errorMessage={intl.formatMessage({
                          id: "enter_vat",
                        })}
                        required
                      />
                    </div>
                    <div className="md:col-span-1">
                      <Select
                        label={intl.formatMessage({ id: "sector" })}
                        options={sectorOptions[role]}
                        value={sector}
                        onChange={(e) => {
                          setSector(e.target.value);
                        }}
                      />
                    </div>
                  </>
                )}
                <div className="md:col-span-1">
                  <Input
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                    }}
                    label={intl.formatMessage({ id: "name" })}
                    type="text"
                    errorMessage={intl.formatMessage({ id: "enter_name" })}
                    required
                  />
                </div>
                <div className="md:col-span-1">
                  <Input
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                    }}
                    label={intl.formatMessage({ id: "surname" })}
                    type="text"
                    errorMessage={intl.formatMessage({
                      id: "enter_surname",
                    })}
                    required
                  />
                </div>
                {role === "user_type_freelance" && (
                  <div className="md:col-span-2">
                    <Select
                      label={intl.formatMessage({ id: "specialization" })}
                      options={sectorOptions["user_type_design_studio"]}
                      value={sector}
                      onChange={(e) => {
                        setSector(e.target.value);
                      }}
                    />
                  </div>
                )}
                {[
                  "user_type_design_studio",
                  "user_type_enterprise",
                  "user_type_resale_tintometer",
                  "user_type_resale",
                ].includes(role) && (
                  <div className="md:col-span-1">
                    <Select
                      label={intl.formatMessage({ id: "role" })}
                      options={qualificationOptions[role]}
                      value={qualification}
                      onChange={(e) => {
                        setQualification(e.target.value);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="mt-8">
                <Button
                  full
                  type="submit"
                  submitting={loading}
                  uppercase={false}
                  disabled={editableRoles.length === 0}
                >
                  <FormattedMessage id="save" defaultMessage="Salva" />
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </>
  );
}
